import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Home, BookOpen } from "lucide-react";

// NOTE: In a CRA SPA the HTTP response is always 200 — the server serves index.html
// for every path. To return a true HTTP 404, configure your reverse proxy (Nginx /
// OpenLiteSpeed) to pass a 404 status for unmatched paths, or migrate to SSR.
// This component maximises SEO safety by injecting <meta name="robots" content="noindex">.
export default function NotFound() {
  useEffect(() => {
    const prev = document.title;
    document.title = "404 — Page Not Found · EDUHUB";

    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);

    return () => {
      document.title = prev;
      meta.remove();
    };
  }, []);

  return (
    <div
      className="max-w-3xl mx-auto px-6 lg:px-12 py-32 text-center"
      data-testid="not-found-page"
    >
      <div className="inline-flex items-center justify-center w-20 h-20 border border-eduhu-border bg-eduhu-bg2 mx-auto mb-8">
        <AlertTriangle size={34} className="text-eduhu-gold" strokeWidth={1.2} />
      </div>

      <div className="overline text-eduhu-claret mb-4">404 — Page Not Found</div>

      <h1 className="font-serif text-6xl lg:text-7xl tracking-tight text-eduhu-dark leading-[0.95]">
        Lost in<br />
        <span className="italic text-eduhu-claret">the curriculum.</span>
      </h1>

      <p className="mt-8 text-lg text-eduhu-inkSoft max-w-sm mx-auto leading-relaxed">
        This page doesn't exist. It may have been moved, deleted, or the link might be incorrect.
      </p>

      <div className="mt-12 flex flex-wrap justify-center gap-4">
        <Link
          to="/"
          className="eduhu-btn-primary"
          data-testid="not-found-home"
        >
          <Home size={15} /> Back to Homepage
        </Link>
        <Link
          to="/courses"
          className="eduhu-btn-ghost"
          data-testid="not-found-courses"
        >
          <BookOpen size={15} /> Browse Courses
        </Link>
      </div>

      <div
        className="mt-24 font-serif text-[clamp(96px,20vw,160px)] leading-none text-eduhu-border select-none pointer-events-none"
        aria-hidden="true"
      >
        404
      </div>
    </div>
  );
}
